/**
 * Vault Service
 *
 * API service for document and file management.
 */

import type { EvidenceRetentionNotice } from '@tonnie/foundation';
import { VAULT_MAX_FILE_BYTES } from '@tonnie/schemas';
import { TonnieApiError } from '../client/api-error';
import { TonnieClient, type UploadOptions } from '../client/base';
import { UploadTooLargeError } from '../client/upload';
import type { UploadProgress } from '../client/upload';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

export type VaultHeroType = 'checkup' | 'teleportal' | 'qultureup' | 'care' | 'passport' | 'career';
export type VaultDocumentStatus = 'uploaded' | 'validated' | 'rejected' | 'pending';
export type VaultTelePortalTrack = 'legal' | 'travel' | 'setup';

/** What deleting a vault file returns (W5.8). */
export interface VaultDeleteResult {
  success: true;
  fileId: string;
  evidenceRetention: EvidenceRetentionNotice[];
}

export interface DocumentTag {
  id: string;
  label: string;
  color: string;
}

export interface VaultFile {
  id: string;
  fileName: string;
  fileType: string;
  documentType: string;
  hero: VaultHeroType | string;
  subTopic?: string;
  teleportalTrack?: VaultTelePortalTrack | string;
  category: string;
  subcategory?: string;
  status: VaultDocumentStatus;
  fileSize: number;
  contentType: string;
  mimeType?: string;
  tonnieChecked: boolean;
  uploadedAt: string;
  uploadedBy?: string;
  tags: DocumentTag[];
  validationResult?: {
    completeness?: boolean;
    expiry?: string;
    consistency?: string[];
    warnings?: string[];
  };
  teleportalTaskId?: string;
  verifiedAt?: string;
  expiresAt?: string;
  documentMetadata?: {
    location?: string;
    validityDays?: number;
    requiresCertification?: boolean;
    certificationType?: string;
    issuingAuthorityKey?: string;
  };
}

export interface VaultCategory {
  id: string;
  name: string;
  description?: string;
  icon?: string;
}

export interface VaultStorageUsage {
  used: number;
  limit: number;
  percentage: number;
  fileCount: number;
  byCategory?: Array<{ category: string; size: number; count: number }>;
}

export interface UploadFileInput {
  fileName: string;
  fileType: string;
  category: string;
  subcategory?: string;
  mimeType?: string;
  fileSize?: number;
}

/** Per-call upload controls (progress, cancellation, stall window). */
export type VaultUploadOptions = Pick<UploadOptions, 'onProgress' | 'signal' | 'stallTimeoutMs'>;

export type VaultUploadProgress = UploadProgress;

export interface UploadUrlResponse {
  fileId: string;
  uploadUrl: string;
  storageKey: string;
  expiresIn: number;
}

export interface DownloadUrlResponse {
  fileId: string;
  fileName: string;
  downloadUrl: string;
  expiresIn: number;
}

// ═══════════════════════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════════════════════

export class VaultService {
  constructor(private client: TonnieClient) {}

  /**
   * List files in vault
   */
  async listFiles(params?: {
    category?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ files: VaultFile[]; total: number }> {
    return this.client.get<{ files: VaultFile[]; total: number }>('/api/vault/files', {
      params: params as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * Get file details
   */
  async getFile(fileId: string): Promise<VaultFile> {
    return this.client.get<VaultFile>(`/api/vault/files/${fileId}`);
  }

  /**
   * Get presigned download URL
   */
  async getDownloadUrl(fileId: string): Promise<DownloadUrlResponse> {
    return this.client.get<DownloadUrlResponse>(`/api/vault/files/${fileId}/download`);
  }

  /**
   * Get presigned upload URL
   */
  async getUploadUrl(input: UploadFileInput): Promise<UploadUrlResponse> {
    return this.client.post<UploadUrlResponse>('/api/vault/files/upload-url', input);
  }

  /**
   * Confirm file upload completed
   */
  async confirmUpload(
    fileId: string
  ): Promise<{ success: boolean; fileId: string; status: string; size: number }> {
    return this.client.post<{ success: boolean; fileId: string; status: string; size: number }>(
      `/api/vault/files/${fileId}/confirm`
    );
  }

  /**
   * Proxy upload — uploads file data through the API server to S3.
   * Avoids CORS issues when uploading from browser clients (e.g. Expo web).
   */
  // Web only. React Native cannot upload a device file through a JS Blob/
  // FormData (RN's Blob can't wrap file bytes; its FormData→native multipart
  // bridge rejects file parts). The mobile app streams the file with the native
  // uploader instead — see apps/citizen-mobile/core/lib/vault-upload.ts.
  async uploadViaProxy(
    fileId: string,
    data: Blob | File,
    contentType: string,
    options: VaultUploadOptions = {}
  ): Promise<{ success: boolean; fileId: string; status: string }> {
    const formData = new FormData();
    const blob = data instanceof Blob ? data : new Blob([data], { type: contentType });
    formData.append('file', blob, 'upload');
    return this.client.postForm<{ success: boolean; fileId: string; status: string }>(
      `/api/vault/files/${fileId}/upload-proxy`,
      formData,
      options
    );
  }

  /**
   * Presign → direct PUT → confirm. The WEB upload path.
   *
   * Why this exists (H3/H4). The proxy buffers the ENTIRE file in platform-api
   * memory and then uploads it to S3 a second time, so every vault byte crosses
   * the API host twice and a 76 MB pitch video is 76 MB of heap on a shared
   * process. It also means `confirmUpload` has never run for a production
   * flow: the proxy moves the row to `pending_verification` itself, so the
   * confirm endpoint's S3 existence check — the thing that turns a failed
   * upload into an honest `rejected` instead of a silent orphan — was dead
   * code. This path restores it.
   *
   * The proxy is NOT removed: the native clients upload through it (DW5-1).
   *
   * Progress is reported across the whole three-step flow, with the PUT — the
   * only step whose duration depends on the file — mapped to 5–95 %.
   */
  async uploadDirect(
    input: UploadFileInput,
    data: Blob | File,
    options: VaultUploadOptions = {}
  ): Promise<{ fileId: string; status: string; size: number }> {
    const contentType = input.mimeType || 'application/octet-stream';
    const report = options.onProgress;

    // ── L4 region: F39/N17 refuse an oversize file before any request ──
    // The presign would refuse it anyway, as a generic validation 400 the
    // caller could only render as "upload failed" — and only after a request.
    if (data.size > VAULT_MAX_FILE_BYTES) {
      throw new UploadTooLargeError(
        new TonnieApiError({
          message: `File is ${data.size} bytes; the vault accepts at most ${VAULT_MAX_FILE_BYTES}`,
          status: 413,
          code: 'UPLOAD_TOO_LARGE',
          details: { fileSize: data.size, maxBytes: VAULT_MAX_FILE_BYTES },
        })
      );
    }
    // ── end L4 region ──

    report?.({ loaded: 0, total: data.size, fraction: 0.05 });
    const { fileId, uploadUrl } = await this.getUploadUrl(input);

    await this.client.putPresigned(uploadUrl, data, contentType, {
      signal: options.signal,
      stallTimeoutMs: options.stallTimeoutMs,
      onProgress: (p) =>
        report?.({ loaded: p.loaded, total: p.total, fraction: 0.05 + p.fraction * 0.9 }),
    });

    const confirmed = await this.confirmUpload(fileId);
    report?.({ loaded: data.size, total: data.size, fraction: 1 });
    return { fileId, status: confirmed.status, size: confirmed.size };
  }

  /**
   * Delete a file.
   *
   * `evidenceRetention` (W5.8 · Art. 12(4)) lists every company that still
   * retains the file under a decision record — which company, what, until
   * when and why. The file leaves the citizen's vault either way; an empty
   * list means no company retains it.
   */
  async deleteFile(fileId: string): Promise<VaultDeleteResult> {
    return this.client.delete<VaultDeleteResult>(`/api/vault/files/${fileId}`);
  }

  /**
   * Get file categories
   */
  async getCategories(): Promise<VaultCategory[]> {
    return this.client.get<VaultCategory[]>('/api/vault/categories');
  }

  /**
   * Get storage usage
   */
  async getStorageUsage(): Promise<VaultStorageUsage> {
    return this.client.get<VaultStorageUsage>('/api/vault/storage');
  }

  /**
   * Bundle several vault files into a single ZIP (talent-scoped server-side).
   * Returns the archive as a Blob so callers trigger a browser download via
   * `saveBlob` — mirrors the Mobility / TelePortal bulk-download contract.
   */
  async downloadFilesZip(fileIds: string[]): Promise<Blob> {
    return this.client.post('/api/vault/files/download-zip', { fileIds }, { responseType: 'blob' });
  }
}

export function createVaultService(client: TonnieClient): VaultService {
  return new VaultService(client);
}
